import { useState } from "react";
import { Link } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { PRODUCT_NAME } from "@shared/brand";
import { AlertTriangle } from "lucide-react";

/**
 * F&F testing banner (s117 gate 1). Persistent amber warning bar across the top of
 * every authenticated page, F&F-only. Copy verbatim from
 * `docs/living/ff-onboarding-copy.md` v3 §2.
 *
 * Dismiss is per-session React state (NOT sessionStorage) — resetting on remount
 * means it reappears on page refresh / next login, per spec. Dismissal persists
 * across in-app navigation because the banner instance is stable at the App level.
 * Removed at launch when the F&F flag is cleared.
 */
export function FFTestingBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  if (!user?.isFAndF || dismissed) return null;

  return (
    <div
      role="status"
      data-testid="ff-testing-banner"
      className="w-full text-[13px] leading-snug"
      style={{ backgroundColor: "#FBF3E2", borderBottom: "1px solid #E8CF9E", color: "#6B4E1A" }}
    >
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-center gap-x-2 gap-y-1 px-4 py-2 text-center">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" style={{ color: "#C8862E" }} />
        <span>
          <strong>You're testing {PRODUCT_NAME}.</strong> Use placeholder data only — don't enter real
          SIN, passwords, or account numbers. Database will be wiped before public launch.
        </span>
        <Link
          href="/ff-welcome"
          className="whitespace-nowrap font-medium underline"
          style={{ color: "#B0741C" }}
          data-testid="ff-banner-learn-more"
        >
          Learn more
        </Link>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="whitespace-nowrap font-medium underline"
          style={{ color: "#B0741C" }}
          data-testid="ff-banner-dismiss"
        >
          Dismiss for this session
        </button>
      </div>
    </div>
  );
}
